import { useTranslation } from "react-i18next";
import { useStore } from "@/lib/store";
import { Layers, Wand2, Image as ImageIcon, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { uploadFile } from "@/lib/queryClient";

const FLOW_OPTIONS = [
  "hook-truth-pivot-cta",
  "problem-agitation-solution",
  "behind-the-scenes",
  "live-promo-teaser",
  "case-study",
  "custom",
];

type ImageProviderId =
  | "nano-banana"
  | "nano-banana-pro"
  | "nano-banana-2"
  | "gpt-image-1"
  | "gpt-image-2";

const PROVIDER_OPTIONS: { id: ImageProviderId; label: string }[] = [
  { id: "nano-banana-pro", label: "Nano Banana Pro" },
  { id: "nano-banana-2", label: "Nano Banana 2" },
  { id: "gpt-image-2", label: "GPT Image 2" },
];

const MAX_REFERENCE_IMAGES = 7;

interface SignedUrlEntry {
  r2Key: string;
  url: string;
}

export default function StoriesControlsBar() {
  const { t } = useTranslation();
  const {
    storiesFrameCount,
    storiesFlowId,
    storiesBackgroundMode,
    storiesImageProvider,
    storiesUserBackgroundR2Keys,
    storiesUserBackgroundMode,
    storiesUserBackgroundAssignments,
    setStoriesFrameCount,
    setStoriesFlowId,
    setStoriesBackgroundMode,
    setStoriesImageProvider,
    setStoriesUserBackgroundMode,
    addStoriesUserBackgroundR2Key,
    removeStoriesUserBackgroundR2Key,
    cycleStoriesUserBackgroundAssignment,
  } = useStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [signedUrls, setSignedUrls] = useState<SignedUrlEntry[]>([]);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (storiesUserBackgroundR2Keys.length >= MAX_REFERENCE_IMAGES) break;
        const formData = new FormData();
        formData.append("files", file);
        const res = (await uploadFile("/api/graphics/upload", formData)) as {
          items?: { r2Key: string; signedUrl?: string }[];
        };
        const item = res?.items?.[0];
        if (item?.r2Key) {
          addStoriesUserBackgroundR2Key(item.r2Key);
          if (item.signedUrl) {
            setSignedUrls((prev) => [
              ...prev.filter((u) => u.r2Key !== item.r2Key),
              { r2Key: item.r2Key, url: item.signedUrl! },
            ]);
            // VP-230 4b: podgląd klatek pokazuje wgrane tło jeszcze przed generacją
            useStore.getState().setStoriesUserBackgroundPreview(item.r2Key, item.signedUrl);
          }
        }
      }
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const slotsLeft = MAX_REFERENCE_IMAGES - storiesUserBackgroundR2Keys.length;

  return (
    <div className="flex flex-nowrap items-center gap-2 px-4 py-2 border-b border-white/[0.06] bg-white/[0.02] text-[11px] overflow-x-auto scrollbar-none min-w-0">
      <div className="flex items-center gap-1.5 text-white/60">
        <Layers size={12} />
        <span className="font-medium">{t("storiesCreator.controls.frameCount", "Klatki")}:</span>
      </div>
      <div className="flex items-center gap-1">
        {[3, 4, 5, 6, 7].map((n) => (
          <button
            key={n}
            onClick={() => setStoriesFrameCount(n)}
            className={`w-7 h-7 rounded-md text-[11px] font-semibold transition-all ${
              storiesFrameCount === n
                ? "bg-[#ef3e29] text-white"
                : "bg-white/[0.04] text-white/50 hover:bg-white/[0.08]"
            }`}
            data-testid={`btn-frame-count-${n}`}
          >
            {n}
          </button>
        ))}
      </div>

      <div className="w-px h-4 bg-white/[0.08]" />

      <select
        value={storiesFlowId}
        onChange={(e) => setStoriesFlowId(e.target.value)}
        className="bg-white/[0.04] hover:bg-white/[0.08] text-white/80 text-[11px] rounded-md px-2 py-1 border border-white/[0.06] outline-none cursor-pointer"
        data-testid="select-stories-flow"
      >
        {FLOW_OPTIONS.map((id) => (
          <option key={id} value={id} className="bg-[#0a0c10]">
            {t(`storiesCreator.flow.${id}`, id)}
          </option>
        ))}
      </select>

      <div className="w-px h-4 bg-white/[0.08]" />

      <button
        onClick={() =>
          setStoriesBackgroundMode(storiesBackgroundMode === "consistent" ? "varied" : "consistent")
        }
        className="flex items-center justify-center gap-1.5 min-w-[96px] bg-white/[0.04] hover:bg-white/[0.08] text-white/80 text-[11px] rounded-md px-2 py-1 border border-white/[0.06]"
        data-testid="btn-toggle-bg-mode"
      >
        <ImageIcon size={11} />
        {storiesBackgroundMode === "consistent"
          ? t("storiesCreator.bgMode.consistent", "Spójna seria")
          : t("storiesCreator.bgMode.varied", "Różne tła")}
      </button>

      <select
        value={storiesImageProvider}
        onChange={(e) =>
          useStore.getState().setStoriesImageProvider(e.target.value as ImageProviderId)
        }
        className="bg-white/[0.04] hover:bg-white/[0.08] text-white/80 text-[11px] rounded-md px-2 py-1 border border-white/[0.06] outline-none cursor-pointer"
        data-testid="select-stories-provider"
      >
        {PROVIDER_OPTIONS.map((p) => (
          <option key={p.id} value={p.id} className="bg-[#0a0c10]">
            <Wand2 className="inline mr-1" /> {p.label}
          </option>
        ))}
      </select>

      <div className="w-px h-4 bg-white/[0.08]" />

      <div className="flex flex-nowrap items-center gap-1.5 flex-shrink-0">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleUpload}
          data-testid="input-stories-bg-upload"
        />

        {storiesUserBackgroundR2Keys.length > 0 && (
          <div className="flex items-center gap-1">
            {storiesUserBackgroundR2Keys.map((r2Key) => {
              const signed = signedUrls.find((u) => u.r2Key === r2Key);
              const assignedFrame = storiesUserBackgroundAssignments[r2Key];
              return (
                <div
                  key={r2Key}
                  className="relative w-12 h-12 rounded-md overflow-hidden border border-emerald-500/30 bg-black/40 group"
                  data-testid={`stories-bg-thumb-${r2Key}`}
                >
                  {signed?.url ? (
                    <img src={signed.url} alt="reference" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-emerald-400/60">
                      <ImageIcon size={12} />
                    </div>
                  )}

                  {/* Frame-assignment badge — click to cycle Auto → 1 → … → N → Auto */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      cycleStoriesUserBackgroundAssignment(r2Key, storiesFrameCount);
                    }}
                    className={`absolute top-0 left-0 px-1 min-w-[16px] h-4 text-[9px] font-bold leading-4 text-center ${
                      assignedFrame
                        ? "bg-[#ef3e29] text-white"
                        : "bg-black/70 text-white/70 hover:bg-black/90"
                    }`}
                    data-testid={`btn-assign-frame-${r2Key}`}
                    title={
                      assignedFrame
                        ? t("storiesCreator.assign.assignedTo", "Klatka {{n}}", {
                            n: assignedFrame,
                          })
                        : t("storiesCreator.assign.auto", "Auto (round-robin)")
                    }
                  >
                    {assignedFrame ?? "A"}
                  </button>

                  <button
                    onClick={() => removeStoriesUserBackgroundR2Key(r2Key)}
                    className="absolute top-0 right-0 w-4 h-4 flex items-center justify-center bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity text-white"
                    data-testid={`btn-remove-stories-bg-${r2Key}`}
                  >
                    <X size={10} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {slotsLeft > 0 && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-white/70 text-[11px] rounded-md px-2 py-1 border border-white/[0.06] disabled:opacity-50"
            data-testid="btn-upload-stories-bg"
          >
            <Upload size={11} />
            {storiesUserBackgroundR2Keys.length === 0
              ? t("storiesCreator.controls.uploadBackground", "Wgraj zdjęcia (1-7)")
              : t("storiesCreator.controls.addMore", "Dodaj kolejne")}
          </button>
        )}

        {storiesUserBackgroundR2Keys.length > 0 && (
          <span className="text-[10px] text-white/40">
            {storiesUserBackgroundR2Keys.length}/{MAX_REFERENCE_IMAGES}
          </span>
        )}

        {storiesUserBackgroundR2Keys.length > 0 && (
          <button
            onClick={() =>
              setStoriesUserBackgroundMode(
                storiesUserBackgroundMode === "as-is" ? "variation" : "as-is"
              )
            }
            className={`flex items-center gap-1.5 text-[11px] rounded-md px-2 py-1 border transition-colors ${
              storiesUserBackgroundMode === "as-is"
                ? "bg-emerald-500/10 hover:bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                : "bg-white/[0.04] hover:bg-white/[0.08] text-white/80 border-white/[0.06]"
            }`}
            data-testid="btn-toggle-user-bg-mode"
            title={
              storiesUserBackgroundMode === "as-is"
                ? t("storiesCreator.userBgMode.asIsHint", "Twoje zdjęcia użyte 1:1, bez AI")
                : t(
                    "storiesCreator.userBgMode.variationHint",
                    "AI tworzy wariacje na bazie Twoich zdjęć"
                  )
            }
          >
            {storiesUserBackgroundMode === "as-is" ? (
              <>
                <ImageIcon size={11} />
                {t("storiesCreator.userBgMode.asIs", "Moje zdjęcia (bez AI)")}
              </>
            ) : (
              <>
                <Wand2 size={11} />
                {t("storiesCreator.userBgMode.variation", "AI wariacje")}
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
